import React, { useState, useMemo } from "react";
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  OutlinedInput,
  Popover,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import AddCustomOption from "../../product/component/AddCustomOption";

export default function CategoryFilterSelect(props) {
  const {
    value,
    onChange,
    options = [],
    categoryKey = "category",
    label,
    placeholder,
    required,
    disabled,
    helperText,
    fullWidth = true,
    renderOption,
    renderValue,
    customOption, // { variantKey, label, onChange }
  } = props;

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");

  const open = Boolean(anchorEl);

  // Extract unique categories from options
  const categories = useMemo(() => {
    const cats = new Set();
    options.forEach((option) => {
      const cat = option[categoryKey];
      if (cat) {
        cats.add(cat);
      }
    });
    return ["All", ...Array.from(cats).sort()];
  }, [options, categoryKey]);

  // Filter options based on selected category
  const filteredOptions = useMemo(() => {
    if (selectedCategory === "All") {
      return options;
    }
    return options.filter((option) => option[categoryKey] === selectedCategory);
  }, [options, selectedCategory, categoryKey]);

  const handleClick = (event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setSelectedCategory("All");
    setAnchorEl(null);
  };

  const handleSelect = (option) => {
    onChange(option);
    handleClose();
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onChange(null);
  };

  // Get display value
  const displayValue = useMemo(() => {
    if (!value) return "";
    if (renderValue) {
      return renderValue(value);
    }
    return value.name || "";
  }, [value, renderValue]);

  return (
    <FormControl variant="outlined" fullWidth={fullWidth} required={required} disabled={disabled}>
      <InputLabel shrink={Boolean(displayValue) || open}>{label}</InputLabel>
      <OutlinedInput
        readOnly
        onClick={handleClick}
        value={displayValue}
        label={label}
        placeholder={placeholder}
        notched={Boolean(displayValue) || open}
        endAdornment={
          <InputAdornment position="end">
            {value ? (
              <IconButton size="small" onClick={handleClear} edge="end">
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : customOption ? (
              <span onClick={(e) => e.stopPropagation()}>
                <AddCustomOption
                  variantKey={customOption.variantKey}
                  label={customOption.label}
                  onChange={customOption.onChange}
                >
                  <IconButton size="small" edge="end">
                    <PlaylistAddIcon fontSize="small" />
                  </IconButton>
                </AddCustomOption>
              </span>
            ) : (
              <KeyboardArrowDownIcon sx={{ color: "action.active" }} />
            )}
          </InputAdornment>
        }
        sx={{
          cursor: disabled ? "default" : "pointer",
          "& input": {
            cursor: disabled ? "default" : "pointer",
          },
        }}
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              width: anchorEl?.offsetWidth || 300,
              maxHeight: 400,
              mt: 0.5,
            },
          },
        }}
      >
        {/* Category Chips */}
        {categories.length > 2 && (
          <Box
            sx={{
              p: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            {categories.map((category) => (
              <Chip
                key={category}
                label={category}
                size="small"
                variant={selectedCategory === category ? "filled" : "outlined"}
                color={selectedCategory === category ? "primary" : "default"}
                onClick={() => setSelectedCategory(category)}
                sx={{ cursor: "pointer" }}
              />
            ))}
          </Box>
        )}

        {/* Options List */}
        <List
          sx={{
            maxHeight: categories.length > 2 ? 300 : 350,
            overflow: "auto",
            py: 0,
          }}
        >
          {filteredOptions.length === 0 ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2" color="textSecondary">
                No options available
              </Typography>
            </Box>
          ) : (
            filteredOptions.map((option, idx) => (
              <ListItemButton
                key={idx}
                selected={value?.name === option.name}
                onClick={() => handleSelect(option)}
                sx={{
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  "&:last-child": {
                    borderBottom: "none",
                  },
                }}
              >
                {renderOption ? (
                  renderOption(option)
                ) : (
                  <ListItemText primary={option.name} secondary={option.description} />
                )}
              </ListItemButton>
            ))
          )}
        </List>
      </Popover>
    </FormControl>
  );
}
